import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { useState } from 'react';

import { IconButton } from '../../components/icon-button';
import { IconSearch } from '../../components/icons';
import { generateTestId } from '../../lib/test-helpers';
import { cn } from '../lib/utils';

const inputVariants = cva(
  cn(
    'bg-background flex h-10 w-full flex-row overflow-hidden rounded-xs text-base',
    'text-foreground focus-within:border-wtw-stratosphere-450 hover:focus-within:border-wtw-stratosphere-450 border',
  ),
  {
    variants: {
      variant: {
        primary: '',
        withAiSuggestion: 'border-purple-500 bg-purple-100 dark:bg-purple-500',
      },
      isInvalid: {
        true: 'border-fire-500',
      },
      disabled: {
        true: 'bg-muted',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);

interface InputProps extends React.ComponentProps<'input'>, VariantProps<typeof inputVariants> {
  appendText?: string;
  prependText?: string;
  testId?: string;
  fieldId?: string; // Used for id and to generate test-id (if not provided) on input
  disabled?: boolean;
  appendIcon?: React.ReactNode;
  wrapperClassName?: string;
  inputClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      appendText,
      prependText,
      onChange,
      disabled,
      testId: propTestId,
      fieldId,
      value,
      isInvalid,
      variant,
      appendIcon,
      wrapperClassName,
      inputClassName,
      ...props
    },
    ref,
  ) => {
    const testId = propTestId ?? generateTestId(fieldId, 'input');
    const [passwordIsVisible, setPasswordIsVisible] = useState<boolean>(false);
    // Used to toggle between password and text type to show password
    const updatedType = type === 'password' && passwordIsVisible ? 'text' : type;
    return (
      <div className={cn(inputVariants({ isInvalid, disabled, variant, className: wrapperClassName }))}>
        {prependText && (
          <div className="text-steel-300 flex shrink-0 items-center justify-center border-r px-2">{prependText}</div>
        )}
        {type === 'search' && (
          <div className="text-steel-500 flex items-center justify-center pl-3">
            <IconSearch size={16} />
          </div>
        )}
        <input
          data-testid={testId}
          id={fieldId}
          type={updatedType}
          className={cn(
            `ring-offset-background file:text-foreground placeholder:text-steel-300 flex w-full grow bg-transparent`,
            `px-3 py-2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:font-light`,
            `focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50`,
            inputClassName,
          )}
          aria-invalid={isInvalid ?? undefined}
          ref={ref}
          value={value}
          onChange={onChange}
          disabled={disabled}
          {...props}
        />
        {appendText && (
          <div className="text-steel-300 flex shrink-0 items-center justify-center border-l px-2">{appendText}</div>
        )}
        {type === 'password' && (
          <div className="flex items-center justify-center pr-3">
            <IconButton
              type={passwordIsVisible ? 'hidePassword' : 'showPassword'}
              onClick={() => setPasswordIsVisible(!passwordIsVisible)}
              ariaLabel={passwordIsVisible ? 'Hide password' : 'Show password'}
              title={passwordIsVisible ? 'Hide password' : 'Show password'}
            />
          </div>
        )}
        {appendIcon && <div className="flex items-center justify-center pr-3">{appendIcon}</div>}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
